import React, { useState, useEffect } from 'react';
import api from '../../services/api'; // Make sure this path is correct
import Select from 'react-select';
import * as XLSX from 'xlsx';

import SideNav, { METRIC_CONFIG } from './SideNav';
import AdRecallAllFiles from './AdRecallAllFiles';
import FilterManager from './FilterManager';
import SpssSyntaxEditor from './SpssSyntaxEditor';

const styles = {
    pageContainer: { display: 'flex' },
    mainContent: { flex: 1, padding: '20px' },
    filterContainer: { display: 'flex', flexDirection: 'column', gap: '15px', padding: '15px', backgroundColor: '#f9fafb', borderRadius: '12px', border: '1px solid #e5e7eb', marginBottom: '16px' },
    filterRow: { display: 'flex', gap: '20px', alignItems: 'flex-end', flexWrap: 'wrap' },
    filterGroup: { display: 'flex', flexDirection: 'column', gap: '6px' },
    filterLabel: { fontWeight: 600, color: '#374151' },
    hr: { border: 0, borderTop: '1px solid #e2e8f0', margin: '15px 0' },
    disabled: { opacity: 0.5, cursor: 'not-allowed', pointerEvents: 'none' },
    exportingOverlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(255, 255, 255, 0.8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2000,
        fontSize: '1.2rem',
        color: '#111827',
        fontWeight: 600,
    },
    // Styles for the new Data Quality Warning Modal
    dataQualityOverlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 3000,
    },
    dataQualityModal: {
        backgroundColor: 'white',
        padding: '2rem',
        borderRadius: '12px',
        boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
        maxWidth: '600px',
        width: '90%',
    },
    dataQualityTitle: {
        marginTop: 0,
        color: '#b91c1c',
        fontSize: '1.25rem',
    },
    dataQualityMessage: {
        lineHeight: 1.6,
        color: '#374151',
        whiteSpace: 'pre-wrap',
    },
    dataQualityActions: {
        marginTop: '1.5rem',
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '1rem',
    },
    dataQualityButton: {
        padding: '10px 20px',
        border: 'none',
        borderRadius: '8px',
        cursor: 'pointer',
        fontWeight: 600,
        fontSize: '0.9rem',
        transition: 'background-color 0.2s',
    },
};

const selectStyles = {
    container: (provided) => ({ ...provided, minWidth: '250px', flex: 1 }),
    menu: (provided) => ({ ...provided, zIndex: 5 })
};

const AnalysisPage = () => {
    const [metric, setMetric] = useState('ad_recall');
    const [scoreType, setScoreType] = useState('topBox');
    const [displayType, setDisplayType] = useState('score');
    
    const [savedFilters, setSavedFilters] = useState([]);
    const [selectedFilter, setSelectedFilter] = useState(null);
    const [isFilterManagerOpen, setFilterManagerOpen] = useState(false);
    
    const [filterOptions, setFilterOptions] = useState({ years: [], waves: [], ages: [], genders: [], countries: [], marketClusters: [] });
    const [selectedYears, setSelectedYears] = useState([]);
    const [selectedWaves, setSelectedWaves] = useState([]);
    const [selectedAges, setSelectedAges] = useState([]);
    const [selectedGenders, setSelectedGenders] = useState([]);
    const [selectedCountries, setSelectedCountries] = useState([]);
    const [selectedMarketClusters, setSelectedMarketClusters] = useState([]);
    
    const [data, setData] = useState([]);
    const [percentiles, setPercentiles] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    const [dataQualityError, setDataQualityError] = useState(null);
    const [excludeMissingAds, setExcludeMissingAds] = useState(false);

    const [initialOptionsLoaded, setInitialOptionsLoaded] = useState(false);
    const [isExportingAll, setIsExportingAll] = useState(false);
    const [isSyntaxEditorOpen, setIsSyntaxEditorOpen] = useState(false);

    // Banner selection state
    const [selectedBanners, setSelectedBanners] = useState(['Gender', 'Age_Group', 'Wave']);
    const [selectedNestedBanners, setSelectedNestedBanners] = useState(['Gender_Wave', 'Age_Group_Wave']);
    const [bannerStructure, setBannerStructure] = useState([]);
    const [nestedBannerStructure, setNestedBannerStructure] = useState([]);
    const [totalN, setTotalN] = useState(0);

    const availableBanners = [
        { value: 'Gender', label: 'Gender' },
        { value: 'Income', label: 'Income' },
        { value: 'Age_Group', label: 'Age Group' },
        { value: 'Wave', label: 'Wave' }
    ];

    const availableNestedBanners = [
        { value: 'Gender_Wave', label: 'Gender × Wave' },
        { value: 'Age_Group_Wave', label: 'Age Group × Wave' }
    ];

    useEffect(() => {
        const loadInitialOptions = async () => {
            setLoading(true);
            setError(null);
            try {
                const [optionsRes, savedFiltersRes] = await Promise.all([
                    api.get('/bcm_norms/spss-files/filter-options/'),
                    api.get('/bcm_norms/filters/')
                ]);

                const allFilterOptions = optionsRes.data;
                const findAndFormatOptions = (id) => {
                    const found = allFilterOptions.find(opt => opt.id === id);
                    return found ? found.options.map(o => ({ value: o, label: o })) : [];
                };
                
                const options = {
                    years: findAndFormatOptions('Year'),
                    waves: findAndFormatOptions('Wave_Label'),
                    ages: findAndFormatOptions('Age_Group'),
                    genders: findAndFormatOptions('Gender_Label'),
                    countries: findAndFormatOptions('Country'),
                    marketClusters: findAndFormatOptions('Market_Cluster'),
                };

                setFilterOptions(options);
                setSavedFilters(savedFiltersRes.data);

                if (options.years.length === 0 && options.waves.length === 0 && options.ages.length === 0 && options.genders.length === 0 && options.countries.length === 0) {
                    setError("No filterable data found. Please upload SPSS files with columns like 'Year', 'Wave', 'Age', 'Gender', or 'Country'.");
                    setLoading(false);
                }

            } catch (err) {
                setError(err.message);
                setLoading(false);
            } finally {
                setInitialOptionsLoaded(true);
            }
        };

        loadInitialOptions();
    }, []);

    useEffect(() => {
        if (!initialOptionsLoaded) return;

        const fetchData = async () => {
            setLoading(true);
            setError(null);
            setDataQualityError(null);
            
            const params = new URLSearchParams();
            params.append('metric_type', metric);

            if (selectedFilter?.value) {
                params.append('filter_id', selectedFilter.value);
            }
            if (excludeMissingAds) {
                params.append('exclude_missing_ads', 'true');
            }
            
            selectedYears.forEach(year => params.append('Year', year.value));
            selectedWaves.forEach(wave => params.append('Wave', wave.value));
            selectedAges.forEach(age => params.append('Age', age.value));
            selectedGenders.forEach(gender => params.append('Gender', gender.value));
            selectedCountries.forEach(country => params.append('Country', country.value));
            selectedMarketClusters.forEach(cluster => params.append('Market_Cluster', cluster.value));

            // Add selected banners to request
            selectedBanners.forEach(banner => params.append('banners', banner));
            // Add selected nested banners to request
            selectedNestedBanners.forEach(nested => params.append('nested_banners', nested));

            try {
                const response = await api.get('/bcm_norms/spss-files/ad-recall-top-box-all/', { params });
                setData(response.data.results || []);
                setPercentiles(response.data.percentiles || {});
                setBannerStructure(response.data.banners || []);
                setNestedBannerStructure(response.data.nested_banners || []);
                setTotalN(response.data.total_n || 0);
            } catch (err) {
                if (err.message && err.message.includes("Data quality issue")) {
                    setDataQualityError(err.message);
                } else {
                    setError(err.message);
                }
                setData([]);
                setPercentiles({});
            } finally {
                setLoading(false);
            }
        };

        const timer = setTimeout(fetchData, 300);
        return () => clearTimeout(timer);

    }, [metric, selectedFilter, selectedYears, selectedWaves, selectedAges, selectedGenders, selectedCountries, selectedMarketClusters, initialOptionsLoaded, excludeMissingAds, selectedBanners, selectedNestedBanners]);

    const handleProceedExcluding = () => {
        setDataQualityError(null);
        setExcludeMissingAds(true); // This will trigger the useEffect to refetch
    };

    const handleCancelDataQualityWarning = () => {
        setDataQualityError(null);
        setError("Analysis cancelled due to data quality concerns. Please fix the source files and re-upload.");
    };
    
    const handleFilterChange = (setter) => (value) => {
        setter(value);
        setExcludeMissingAds(false); // Reset exclusion on any manual filter change
    };

    // Helper to get value from cell data based on displayType
    const getCellValue = (cellData, currentDisplayType, currentScoreType) => {
        if (!cellData) return '-';
        const isTopBox = currentScoreType === 'topBox';

        if (currentDisplayType === 'score') {
            const val = isTopBox ? cellData.TopBoxScore : cellData.Top2BoxScore;
            return val !== null && val !== undefined ? val.toFixed(1) : '-';
        } else if (currentDisplayType === 'count') {
            const val = isTopBox ? cellData.TopBoxCount : cellData.Top2BoxCount;
            return val !== null && val !== undefined ? val : '-';
        } else if (currentDisplayType === 'baseSize') {
            const val = cellData.ValidN !== undefined ? cellData.ValidN : cellData.BaseSize;
            return val !== null && val !== undefined ? val : '-';
        }
        return '-';
    };

    const handleExportAllMetrics = async () => {
        setIsExportingAll(true);
        try {
            const workbook = XLSX.utils.book_new();
            const isTopBox = scoreType === 'topBox';
            const scoreKey = isTopBox ? 'TopBoxScore' : 'Top2BoxScore';

            // List of all metrics to export - use names from METRIC_CONFIG
            const metricsToExport = [
                { key: 'ad_recall', name: METRIC_CONFIG.ad_recall.title },
                { key: 'ad_brand_linkage', name: METRIC_CONFIG.ad_brand_linkage.title },
                { key: 'ad_diagnostic_1', name: METRIC_CONFIG.ad_diagnostic_1.title },
                { key: 'ad_diagnostic_2', name: METRIC_CONFIG.ad_diagnostic_2.title },
                { key: 'ad_diagnostic_3', name: METRIC_CONFIG.ad_diagnostic_3.title },
                { key: 'ad_diagnostic_4', name: METRIC_CONFIG.ad_diagnostic_4.title },
                { key: 'ad_diagnostic_5', name: METRIC_CONFIG.ad_diagnostic_5.title },
                { key: 'ad_diagnostic_6', name: METRIC_CONFIG.ad_diagnostic_6.title }
            ];

            for (const metricDef of metricsToExport) {
                // Build params for this metric
                const params = new URLSearchParams();
                params.append('metric_type', metricDef.key);
                if (selectedFilter) params.append('filter_id', selectedFilter.value);
                if (excludeMissingAds) params.append('exclude_missing_ads', 'true');

                selectedYears.forEach(year => params.append('Year', year.value));
                selectedWaves.forEach(wave => params.append('Wave', wave.value));
                selectedAges.forEach(age => params.append('Age', age.value));
                selectedGenders.forEach(gender => params.append('Gender', gender.value));
                selectedCountries.forEach(country => params.append('Country', country.value));
                selectedMarketClusters.forEach(cluster => params.append('Market_Cluster', cluster.value));
                selectedBanners.forEach(banner => params.append('banners', banner));
                selectedNestedBanners.forEach(nested => params.append('nested_banners', nested));

                try {
                    const response = await api.get('/bcm_norms/spss-files/ad-recall-top-box-all/', { params });
                    const metricData = response.data.results || [];
                    const metricPercentiles = response.data.percentiles || {};
                    const metricBanners = response.data.banners || [];
                    const metricNestedBanners = response.data.nested_banners || [];
                    const metricTotalN = response.data.total_n || 0;

                    if (metricData.length === 0) continue;

                    // Build worksheet with nested headers
                    const aoa = [];
                    const merges = [];
                    let colWidths = [{ wch: 40 }];

                    const hasNestedBanners = metricNestedBanners && metricNestedBanners.length > 0;
                    const numHeaderRows = hasNestedBanners ? 3 : (metricBanners.length > 0 ? 2 : 1);

                    const headerRow1 = ['Ad Name'];
                    const headerRow2 = [''];
                    const headerRow3 = [''];

                    let colIndex = 1;

                    // Total column
                    headerRow1.push('Total');
                    headerRow2.push('');
                    headerRow3.push('');
                    if (numHeaderRows > 1) {
                        merges.push({ s: { r: 0, c: colIndex }, e: { r: numHeaderRows - 1, c: colIndex } });
                    }
                    colWidths.push({ wch: 12 });
                    colIndex++;

                    // Simple banners
                    metricBanners.forEach(banner => {
                        const startCol = colIndex;
                        banner.breaks.forEach(breakValue => {
                            headerRow1.push(banner.label);
                            headerRow2.push(breakValue);
                            headerRow3.push('');
                            colWidths.push({ wch: 12 });
                            colIndex++;
                        });
                        if (banner.breaks.length > 1) {
                            merges.push({ s: { r: 0, c: startCol }, e: { r: 0, c: colIndex - 1 } });
                        }
                        if (hasNestedBanners) {
                            for (let i = startCol; i < colIndex; i++) {
                                merges.push({ s: { r: 1, c: i }, e: { r: 2, c: i } });
                            }
                        }
                    });

                    // Nested banners
                    metricNestedBanners.forEach(nested => {
                        const nestedStartCol = colIndex;
                        nested.breaks.forEach(parentBreak => {
                            const parentStartCol = colIndex;
                            parentBreak.children.forEach(childValue => {
                                headerRow1.push(nested.parent_label);
                                headerRow2.push(parentBreak.parent_value);
                                headerRow3.push(childValue);
                                colWidths.push({ wch: 12 });
                                colIndex++;
                            });
                            if (parentBreak.children.length > 1) {
                                merges.push({ s: { r: 1, c: parentStartCol }, e: { r: 1, c: colIndex - 1 } });
                            }
                        });
                        if (colIndex > nestedStartCol) {
                            merges.push({ s: { r: 0, c: nestedStartCol }, e: { r: 0, c: colIndex - 1 } });
                        }
                    });

                    if (numHeaderRows > 1) {
                        merges.push({ s: { r: 0, c: 0 }, e: { r: numHeaderRows - 1, c: 0 } });
                    }

                    aoa.push(headerRow1);
                    if (numHeaderRows >= 2) aoa.push(headerRow2);
                    if (numHeaderRows >= 3) aoa.push(headerRow3);

                    // Total (n) row
                    const totalNRow = ['Total (n)', metricTotalN];
                    metricBanners.forEach(banner => {
                        banner.breaks.forEach(breakValue => {
                            totalNRow.push(banner.totals?.[breakValue] || 0);
                        });
                    });
                    metricNestedBanners.forEach(nested => {
                        nested.breaks.forEach(parentBreak => {
                            parentBreak.children.forEach(childValue => {
                                totalNRow.push(parentBreak.totals?.[childValue] || 0);
                            });
                        });
                    });
                    aoa.push(totalNRow);

                    // Data rows - use current displayType
                    metricData.forEach(row => {
                        const dataRow = [row.AdsName];
                        const totalData = row.scores?.Total;
                        dataRow.push(getCellValue(totalData, displayType, scoreType));

                        metricBanners.forEach(banner => {
                            banner.breaks.forEach(breakValue => {
                                const cellData = row.scores?.[banner.key]?.[breakValue];
                                dataRow.push(getCellValue(cellData, displayType, scoreType));
                            });
                        });

                        metricNestedBanners.forEach(nested => {
                            nested.breaks.forEach(parentBreak => {
                                parentBreak.children.forEach(childValue => {
                                    const cellData = row.scores?.[nested.key]?.[parentBreak.parent_value]?.[childValue];
                                    dataRow.push(getCellValue(cellData, displayType, scoreType));
                                });
                            });
                        });

                        aoa.push(dataRow);
                    });

                    // Empty row + percentiles (only for score display type)
                    if (displayType === 'score') {
                        aoa.push([]);
                        ['Average', '90 Percentile', '75 Percentile', '50 Percentile'].forEach(pKey => {
                            const pRow = [pKey];
                            const totalPVal = metricPercentiles?.Total?.[scoreKey]?.[pKey];
                            pRow.push(totalPVal !== undefined ? totalPVal.toFixed(1) : '-');

                            metricBanners.forEach(banner => {
                                banner.breaks.forEach(breakValue => {
                                    const pVal = metricPercentiles?.[banner.key]?.[breakValue]?.[scoreKey]?.[pKey];
                                    pRow.push(pVal !== undefined ? pVal.toFixed(1) : '-');
                                });
                            });

                            metricNestedBanners.forEach(nested => {
                                nested.breaks.forEach(parentBreak => {
                                    parentBreak.children.forEach(childValue => {
                                        const pVal = metricPercentiles?.[nested.key]?.[parentBreak.parent_value]?.[childValue]?.[scoreKey]?.[pKey];
                                        pRow.push(pVal !== undefined ? pVal.toFixed(1) : '-');
                                    });
                                });
                            });

                            aoa.push(pRow);
                        });
                    }

                    const worksheet = XLSX.utils.aoa_to_sheet(aoa);
                    worksheet['!cols'] = colWidths;
                    worksheet['!merges'] = merges;

                    // Sheet name max 31 chars, remove invalid Excel sheet name characters
                    const sheetName = metricDef.name.replace(/[\\/*?:\[\]]/g, '-').substring(0, 31);
                    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
                } catch (metricErr) {
                    console.warn(`Failed to export ${metricDef.name}:`, metricErr);
                }
            }

            const displayLabel = displayType === 'score' ? 'Score' : (displayType === 'count' ? 'Count' : 'Valid_N');
            const fileName = `All_Metrics_${displayLabel}_${new Date().toISOString().slice(0, 10)}.xlsx`;
            XLSX.writeFile(workbook, fileName);
        } catch (err) {
            console.error("Comprehensive export failed:", err);
            alert(`An error occurred during the export: ${err.message}`);
        } finally {
            setIsExportingAll(false);
        }
    };
    
    const handleFiltersUpdate = (updatedFilters) => {
        setSavedFilters(updatedFilters);
        if (selectedFilter && !updatedFilters.find(f => f.id === selectedFilter.value)) {
            setSelectedFilter(null);
        }
    };

    const handleClearCache = async () => {
        try {
            await api.post('/bcm_norms/spss-files/clear-cache/');
            // Reset all filter options and reload
            setFilterOptions({ years: [], waves: [], ages: [], genders: [], countries: [], marketClusters: [] });
            setInitialOptionsLoaded(false);
            // Trigger reload
            const [optionsRes, savedFiltersRes] = await Promise.all([
                api.get('/bcm_norms/spss-files/filter-options/'),
                api.get('/bcm_norms/filters/')
            ]);
            const allFilterOptions = optionsRes.data;
            const findAndFormatOptions = (id) => {
                const found = allFilterOptions.find(opt => opt.id === id);
                return found ? found.options.map(o => ({ value: o, label: o })) : [];
            };
            setFilterOptions({
                years: findAndFormatOptions('Year'),
                waves: findAndFormatOptions('Wave_Label'),
                ages: findAndFormatOptions('Age_Group'),
                genders: findAndFormatOptions('Gender_Label'),
                countries: findAndFormatOptions('Country'),
                marketClusters: findAndFormatOptions('Market_Cluster'),
            });
            setSavedFilters(savedFiltersRes.data);
            setInitialOptionsLoaded(true);
            alert('Cache cleared! Data refreshed with latest classifications.');
        } catch (err) {
            alert('Failed to clear cache: ' + err.message);
        }
    };

    const isContentDisabled = !initialOptionsLoaded || error || isExportingAll || dataQualityError;

    return (
        <div style={styles.pageContainer}>
            {isExportingAll && (
                <div style={styles.exportingOverlay}>
                    <p>Exporting all metrics, please wait...</p>
                </div>
            )}
            {dataQualityError && (
                <div style={styles.dataQualityOverlay}>
                    <div style={styles.dataQualityModal}>
                        <h3 style={styles.dataQualityTitle}>Data Quality Warning</h3>
                        <p style={styles.dataQualityMessage}>{dataQualityError}</p>
                        <div style={styles.dataQualityActions}>
                            <button
                                style={{...styles.dataQualityButton, backgroundColor: '#f1f5f9', color: '#374151' }}
                                onClick={handleCancelDataQualityWarning}
                            >
                                Cancel
                            </button>
                            <button
                                style={{...styles.dataQualityButton, backgroundColor: '#16a34a', color: 'white' }}
                                onClick={handleProceedExcluding}
                            >
                                Exclude Records & Continue
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <SideNav
                metric={metric}
                setMetric={setMetric}
                scoreType={scoreType}
                setScoreType={setScoreType}
                disabled={isContentDisabled}
                onManageFiltersClick={() => setFilterManagerOpen(true)}
            />
            
            <main style={styles.mainContent}>
                <div style={{ ...styles.filterContainer, ...(isContentDisabled && styles.disabled) }}>
                    <div style={styles.filterRow}>
                         <div style={styles.filterGroup}>
                            <label style={styles.filterLabel}>Saved Filter</label>
                            <Select
                                options={savedFilters.map(f => ({ value: f.id, label: f.name, ...f }))}
                                value={selectedFilter}
                                onChange={handleFilterChange(setSelectedFilter)}
                                styles={selectStyles}
                                placeholder="Use a saved filter..."
                                isClearable
                            />
                        </div>
                        <div style={{ ...styles.filterGroup, justifyContent: 'flex-end' }}>
                            <label style={styles.filterLabel}>&nbsp;</label>
                            <div style={{ display: 'flex', gap: '8px' }}>
                                <button
                                    onClick={() => setIsSyntaxEditorOpen(true)}
                                    style={{ padding: '8px 16px', backgroundColor: '#8b5cf6', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 500 }}
                                >
                                    SPSS Syntax
                                </button>
                                <button
                                    onClick={handleClearCache}
                                    style={{ padding: '8px 16px', backgroundColor: '#3b82f6', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 500 }}
                                >
                                    Refresh Data
                                </button>
                            </div>
                        </div>
                    </div>
                    <hr style={styles.hr} />
                    <div style={styles.filterRow}>
                        <div style={styles.filterGroup}>
                            <label style={styles.filterLabel}>Year</label>
                            <Select isMulti options={filterOptions.years} value={selectedYears} onChange={handleFilterChange(setSelectedYears)} styles={selectStyles} />
                        </div>
                        <div style={styles.filterGroup}>
                            <label style={styles.filterLabel}>Wave</label>
                            <Select isMulti options={filterOptions.waves} value={selectedWaves} onChange={handleFilterChange(setSelectedWaves)} styles={selectStyles} />
                        </div>
                        <div style={styles.filterGroup}>
                            <label style={styles.filterLabel}>Age Group</label>
                            <Select isMulti options={filterOptions.ages} value={selectedAges} onChange={handleFilterChange(setSelectedAges)} styles={selectStyles} />
                        </div>
                        <div style={styles.filterGroup}>
                            <label style={styles.filterLabel}>Gender</label>
                            <Select isMulti options={filterOptions.genders} value={selectedGenders} onChange={handleFilterChange(setSelectedGenders)} styles={selectStyles} />
                        </div>
                        <div style={styles.filterGroup}>
                            <label style={styles.filterLabel}>Country</label>
                            <Select isMulti options={filterOptions.countries} value={selectedCountries} onChange={handleFilterChange(setSelectedCountries)} styles={selectStyles} />
                        </div>
                        <div style={styles.filterGroup}>
                            <label style={styles.filterLabel}>Market Cluster</label>
                            <Select isMulti options={filterOptions.marketClusters} value={selectedMarketClusters} onChange={handleFilterChange(setSelectedMarketClusters)} styles={selectStyles} />
                        </div>
                    </div>
                </div>

                {/* Banner Selection */}
                <div style={{ ...styles.filterContainer, marginBottom: '16px', padding: '12px 15px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '10px' }}>
                        <span style={{ fontWeight: 600, color: '#374151', fontSize: '0.9rem' }}>Simple Banners:</span>
                        {availableBanners.map(banner => (
                            <label key={banner.value} style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                                <input
                                    type="checkbox"
                                    checked={selectedBanners.includes(banner.value)}
                                    onChange={(e) => {
                                        if (e.target.checked) {
                                            setSelectedBanners(prev => [...prev, banner.value]);
                                        } else {
                                            setSelectedBanners(prev => prev.filter(b => b !== banner.value));
                                        }
                                    }}
                                    style={{ width: '16px', height: '16px', accentColor: '#3b82f6' }}
                                />
                                <span style={{ fontSize: '0.9rem', color: '#374151' }}>{banner.label}</span>
                            </label>
                        ))}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: 600, color: '#374151', fontSize: '0.9rem' }}>Nested Banners:</span>
                        {availableNestedBanners.map(nested => (
                            <label key={nested.value} style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                                <input
                                    type="checkbox"
                                    checked={selectedNestedBanners.includes(nested.value)}
                                    onChange={(e) => {
                                        if (e.target.checked) {
                                            setSelectedNestedBanners(prev => [...prev, nested.value]);
                                        } else {
                                            setSelectedNestedBanners(prev => prev.filter(b => b !== nested.value));
                                        }
                                    }}
                                    style={{ width: '16px', height: '16px', accentColor: '#10b981' }}
                                />
                                <span style={{ fontSize: '0.9rem', color: '#374151' }}>{nested.label}</span>
                            </label>
                        ))}
                    </div>
                </div>

                <AdRecallAllFiles
                    loading={loading}
                    error={error}
                    data={data}
                    percentiles={percentiles}
                    metric={metric}
                    scoreType={scoreType}
                    displayType={displayType}
                    setDisplayType={setDisplayType}
                    onExportAllMetrics={handleExportAllMetrics}
                    isExporting={isExportingAll}
                    bannerStructure={bannerStructure}
                    nestedBannerStructure={nestedBannerStructure}
                    totalN={totalN}
                />
            </main>

            <FilterManager
                isOpen={isFilterManagerOpen}
                onClose={() => setFilterManagerOpen(false)}
                onFiltersUpdate={handleFiltersUpdate}
            />

            <SpssSyntaxEditor
                isOpen={isSyntaxEditorOpen}
                onClose={() => setIsSyntaxEditorOpen(false)}
                onSyntaxExecuted={handleClearCache}
            />
        </div>
    );
};

export default AnalysisPage;